import { useState, useEffect } from 'react';
import { Box, FormControl, MenuItem, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb'; // Importar el locale en-gb
import { CustomDatePicker, CustomTextField } from '../../components/Inputs';
import { STPSelect } from '../../components/Select';
import { getCentros } from '../../services/getCentros';
import { getCentroAlumno } from '../../services/getCentrosAlumno';
import { getNivFor } from '../../services/getNivFor';
import { getAllNivFor } from '../../services/getAllNivFor';
import { getPoblaciones } from '../../services/getPoblaciones';
import useUserStore from '../../store/UserStore';
import { useTranslation } from 'react-i18next';

export function PersonalFields({ formData, handleInputChange, fieldErrors }) {
  const { t } = useTranslation();
  const user = useUserStore(state => state.user);

  const [centros, setCentros] = useState([]);
  const [formacions, setFormacions] = useState([]);
  const [poblaciones, setPoblaciones] = useState([]);

  const [centroAlumno, setCentroAlumno] = useState('');
  const [formacionAlumno, setFormacionAlumno] = useState('');

  // Convertir de YYYY-MM-DD a objeto Dayjs
  const parseDate = (dateString) => {
    if (!dateString) return null;
    return dayjs(dateString, 'YYYY-MM-DD'); // Parse desde el formato de la base de datos
  };

  // Convertir de objeto Dayjs a YYYY-MM-DD
  const formatDateToDB = (date) => {
    if (!date) return '';
    return date.format('YYYY-MM-DD'); // Formatear para guardar en la base de datos
  };

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = formatDateToDB(date); // Convertir a 'YYYY-MM-DD'
      handleInputChange('dataNaixement', formattedDate);
    } else {
      handleInputChange('dataNaixement', '');
    }
  };

  // Función para ordenar los arrays recibidos en orden alfabético
  const sortByName = (array) => {
    return array.sort((a, b) => {
      if (a.nombre) {
          if (a.nombre.toLowerCase() < b.nombre.toLowerCase()) {
            return -1;
          }
          if (a.nombre.toLowerCase() > b.nombre.toLowerCase()) {
            return 1;
          }
          return 0;
        }
      else if (a.valor) {
          if (a.valor.toLowerCase() < b.valor.toLowerCase()) {
            return -1;
          }
          if (a.valor.toLowerCase() > b.valor.toLowerCase()) {
            return 1;
          }
          return 0;
        }
      });
    }

  useEffect(() => {
    getCentroAlumno()
      .then((data) => {
        setCentroAlumno(data.data[0].centro_estudio.idcentro);
        handleInputChange('centre', data.data[0].centro_estudio.idcentro);
      })
      .catch((error) => {
        console.error('Error fetching centro alumno:', error);
      });

    getCentros()
      .then((data) => {
        data = sortByName(data.data);
        setCentros(data);
      })
      .catch((error) => {
        console.error('Error fetching centros:', error);
      });

    getNivFor()
      .then((data) => {
        setFormacionAlumno(data.data[0].clave);
        handleInputChange('formacio', data.data[0].clave);
      })
      .catch((error) => {
        console.error('Error fetching formacion alumno:', error);
      });

    getPoblaciones()
      .then((data) => {
        data = sortByName(data);
        setPoblaciones(data);
      })
      .catch((error) => {
        console.error('Error fetching poblaciones:', error);
      });

    getAllNivFor()
      .then((data) => {
        setFormacions(data.data);
      })
      .catch((error) => {
        console.error('Error fetching formacions:', error);
      });
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <CustomTextField
        label={t('personalFields.name')}
        margin="normal"
        value={formData.nom}
        error={!!fieldErrors.nom}
        helperText={fieldErrors.nom && t('fields.required')}
        onChange={(e) => handleInputChange('nom', e.target.value)}
      />

      <CustomTextField
        label={t('personalFields.surname')}
        variant="outlined"
        margin="normal"
        value={formData.cognoms}
        error={!!fieldErrors.cognoms}
        helperText={fieldErrors.cognoms && t('fields.required')}
        onChange={(e) => handleInputChange('cognoms', e.target.value)}
      />

      <CustomTextField
        label={t('personalFields.dni')}
        variant="outlined"
        margin="normal"
        value={formData.dni}
        error={!!fieldErrors.dni}
        helperText={fieldErrors.dni && t('fields.required')}
        onChange={(e) => handleInputChange('dni', e.target.value)}
      />
      <Box sx={{ mt:2 }}>
        <Typography variant="h7" sx={{ color: 'black', ml: '12px', fontWeight: 'bold' }}>
          {t('personalFields.birthdate')}:
        </Typography>
      </Box>
      <CustomDatePicker
        value={formData.dataNaixement ? parseDate(formData.dataNaixement) : null} // Convertir de YYYY-MM-DD a Dayjs
        onChange={handleDateChange}
        renderInput={(params) => <CustomTextField {...params} />}
        error={!!fieldErrors.dataNaixement}
        helperText={fieldErrors.dataNaixement && t('fields.required')}
        inputFormat="DD-MM-YYYY"
      />

      <CustomTextField
        label={t('personalFields.address')}
        variant="outlined"
        margin="normal"
        value={formData.adressa}
        error={!!fieldErrors.adressa}
        helperText={fieldErrors.adressa && t('fields.required')}
        onChange={(e) => handleInputChange('adressa', e.target.value)}
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <CustomTextField
          margin="normal"
          label={t('personalFields.postalCode')}
          variant="outlined"
          value={formData.codiPostal}
          error={!!fieldErrors.codiPostal}
          helperText={fieldErrors.codiPostal && t('fields.required')}
          onChange={(e) => handleInputChange('codiPostal', e.target.value)}
        />
        <FormControl fullWidth variant="outlined" margin="normal">
          <Typography variant="subtitle1" sx={{ fontWeight:'bold', color: 'black', ml: '12px' }}>
            {t('personalFields.city')}:
          </Typography>
          <STPSelect
            value={formData.poblacio}
            onChange={(e) => handleInputChange('poblacio', e.target.value)}
          >
            <MenuItem value="">
              {t('personalFields.city')}
            </MenuItem>
            {poblaciones && poblaciones.length > 0 && poblaciones.map((poblacion) => (
              <MenuItem key={poblacion.clave} value={poblacion.clave}>
                {poblacion.valor}
              </MenuItem>
            ))}
          </STPSelect>
        </FormControl>
      </Box>

      <FormControl fullWidth variant="outlined" margin="normal">
        <Typography variant="subtitle1" sx={{ color: 'black', ml: '12px', fontWeight: 'bold' }}>
          {t('personalFields.center')}:
        </Typography>
        <STPSelect
          value={formData.centre}
          onChange={(e) => handleInputChange('centre', e.target.value)}
        >
          <MenuItem value="">
            {t('personalFields.selectCenter')}
          </MenuItem>
          {centros.map((centro) => (
            <MenuItem key={centro.idcentro} value={(centro.idcentro === undefined || centro.idcentro === null || centro.length === 0) ? '' : centro.idcentro}>
              {centro.nombre}
            </MenuItem>
          ))}
        </STPSelect>
      </FormControl>

      <FormControl fullWidth variant="outlined" margin="normal">
        <Typography variant="subtitle1" sx={{ color: 'black', ml: '12px', fontWeight: 'bold' }}>
          {t('personalFields.formation')}:
        </Typography>
        <STPSelect
          value={formData.formacio}
          onChange={(e) => handleInputChange('formacio', e.target.value)}
        >
          <MenuItem value="">
            {t('personalFields.selectFormation')}
          </MenuItem>
          {formacions.map((formacio) => (
            <MenuItem key={formacio.clave} value={formacio.clave}>
              {formacio.valor}
            </MenuItem>
          ))}
        </STPSelect>
      </FormControl>
    </LocalizationProvider>
  );
}
